import { mountVeivoValidationFlow, VeivoValidationFlowHandle } from "@veivo/web-sdk";
import { useEffect, useRef, useState } from "react";
import { Typography } from "@/components/atoms";

/**
 * Public (unauthenticated) host for the Veivo validation flow — the phone half
 * of the desktop→mobile QR handoff (DEV-1313).
 *
 * The wizard step lives behind Kinde auth, so scanning its QR at the car put
 * the driver on a login wall. This page carries no auth and no Redux: the SDK
 * reads `?veivoValidationId=` from the query and the short-lived session token
 * from the URL FRAGMENT (`#veivoSessionToken=`, never sent to a server, read
 * once then scrubbed), and every subsequent call goes straight to Veivo
 * carrying that token. Nothing here can reach Rideminder data.
 *
 * The service URL comes from build config, NOT the link. A base URL taken from
 * the QR would let a crafted link point a phone's camera upload at an attacker's
 * host, which is exactly the trust this page cannot extend.
 */
export const VeivoVerification = () => {
  const hostRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<VeivoValidationFlowHandle | null>(null);
  const [error, setError] = useState<string | null>(null);

  const baseUrl = import.meta.env.VITE_VEIVO_BASE_URL as string | undefined;

  /**
   * Read the link ONCE, on first render.
   *
   * The SDK strips the handoff code from the URL as soon as it redeems it — a
   * spent single-use credential has no business sitting in history. So these
   * cannot be recomputed per render: the code would read as absent the moment
   * after it was used, and an effect keyed on it would tear the flow down and
   * declare the link incomplete.
   *
   * The current QR carries only that code, which identifies the validation by
   * itself, so there is no id in the URL to look for. Older links still have one.
   */
  const [link] = useState(() => ({
    validationId: new URLSearchParams(window.location.search).get("veivoValidationId"),
    hasHandoffCode: new URLSearchParams(window.location.hash.slice(1)).has("h"),
  }));

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    if (!baseUrl) {
      setError("Vehicle verification is not configured for this environment.");
      return;
    }
    if (!link.validationId && !link.hasHandoffCode) {
      setError("This verification link is incomplete. Please re-scan the code shown on your computer.");
      return;
    }

    flowRef.current = mountVeivoValidationFlow(host, {
      service: { url: baseUrl },
      // Omitted for a code-only link: the SDK gets the id back from redeeming
      // the code.
      ...(link.validationId ? { resumeValidationId: link.validationId } : {}),
      // Testing affordance, build-time gated: renders a per-step skip control.
      // The service must ALSO have VEIVO_ALLOW_TEST_SKIP on, so this flag alone
      // cannot bypass anything — and it is unset in production builds.
      allowTestSkip: import.meta.env.VITE_VEIVO_ALLOW_TEST_SKIP === "true",
      onError: (err) => console.error("Veivo flow error:", err),
    });

    return () => {
      void flowRef.current?.dispose();
      flowRef.current = null;
    };
  }, [baseUrl, link]);

  return (
    <div className="flex min-h-screen w-full flex-col bg-white">
      {error ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <Typography variant="paragraph" className="text-center text-danger">
            {error}
          </Typography>
        </div>
      ) : (
        <div ref={hostRef} className="min-h-screen w-full" />
      )}
    </div>
  );
};
